/**
 * The check result for the recipient rows, shown *under* the table rather than
 * in place of it — the sender fixes a row where they typed it, with the row
 * still in front of them.
 *
 * Every number here is derived from the same `checkTypedRows` pass the table's
 * inline marks read, so the banner, the chips, the worklist and the footer can
 * never disagree with each other or with the rows above.
 */

import React from "react";
import { Box, Typography } from "@mui/material";
import { checkStyles } from "../BulkSend.styles";
import tokens from "../BulkSend.tokens";
import {
  BulkRowCheck,
  BulkRowCheckSummary,
} from "../BulkSendTypesAndValidation";
import { requestCountLabel } from "../BulkSendHelper";
import { WarningIcon } from "./BulkSendIcons";

const t = tokens.check;
const s = checkStyles;

interface Props {
  summary: BulkRowCheckSummary;
  /** Focus the offending input in the table above. */
  onFix: (check: BulkRowCheck) => void;
  /** Label for the primary action; the count is appended. */
  /** Absent when the rows came from a file — there is nothing on screen to skip. */
  onSkip?: (index: number) => void;
}

const RecipientsCheckPanel: React.FC<Props> = ({ summary, onFix, onSkip }) => {
  const { checked, ready, invalid, incomplete, blank, flagged } = summary;
  const needsAttention = flagged.length;

  return (
    <>
      <Box sx={s.panel} role="status">
        <Box sx={s.head}>
          <Box sx={s.headIcon}>
            <WarningIcon size={t.headIconSize} color={t.panelIcon} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="span" sx={s.banner}>
              {checked} {checked === 1 ? "row" : "rows"} checked
              {needsAttention > 0 ? ` · ${needsAttention} need attention` : ""}
            </Typography>
            <Typography component="span" sx={s.sub}>
              {needsAttention === 0
                ? "Nothing has been sent yet. Continue when you're ready."
                : onSkip
                  ? "Nothing has been sent yet. Fix a row above or skip it, then continue."
                  : "Nothing has been sent yet. Fix these rows in your file and upload it again to include them."}
            </Typography>
          </Box>

          <Box sx={s.chips}>
            <Box component="span" sx={s.chip("ready")}>
              <Box component="span" sx={s.chipCount}>
                {ready}
              </Box>
              Ready
            </Box>
            {invalid > 0 && (
              <Box component="span" sx={s.chip("invalid")}>
                <Box component="span" sx={s.chipCount}>
                  {invalid}
                </Box>
                Invalid
              </Box>
            )}
            {incomplete > 0 && (
              <Box component="span" sx={s.chip("incomplete")}>
                <Box component="span" sx={s.chipCount}>
                  {incomplete}
                </Box>
                Incomplete
              </Box>
            )}
            {blank > 0 && (
              <Box component="span" sx={s.chip("blank")}>
                <Box component="span" sx={s.chipCount}>
                  {blank}
                </Box>
                Blank
              </Box>
            )}
          </Box>
        </Box>

        {flagged.length > 0 && (
          <Box sx={s.worklist}>
            {flagged.map((check) => (
              <Box key={check.index} sx={s.row}>
                <Box component="span" sx={s.rowLabel}>
                  Row {check.index + 1}
                </Box>
                <Box component="span" sx={s.rowName}>
                  {check.name || "No name yet"}
                </Box>
                <Box component="span" sx={s.rowReason}>
                  {check.reason}
                </Box>
                <Box sx={s.rowActions}>
                  {check.field !== undefined && (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => onFix(check)}
                      sx={s.rowAction(false)}
                    >
                      Fix above
                    </Box>
                  )}
                  {onSkip && (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => onSkip(check.index)}
                      sx={s.rowAction(true)}
                    >
                      Skip
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={s.footer}>
        <Typography component="span" sx={s.footerText}>
          {needsAttention > 0 ? (
            <>
              {onSkip ? "Skip" : "Leave out"} the {needsAttention} flagged{" "}
              {needsAttention === 1 ? "row" : "rows"} and continue with{" "}
              <b>{requestCountLabel(ready)}</b>.
              {blank > 0 ? " Blank rows are ignored." : ""}
            </>
          ) : (
            <>
              Continue with <b>{requestCountLabel(ready)}</b>.
              {blank > 0 ? " Blank rows are ignored." : ""}
            </>
          )}
        </Typography>
      </Box>
    </>
  );
};

export default RecipientsCheckPanel;
